import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  CaretDown, ArrowClockwise, MapPin,
  Airplane, Path, GraduationCap, FirstAid, Camera, Storefront, Train,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const POI_FIELDS = ["airports", "highways", "universities", "hospitals", "tourism", "commercial", "transport"];
const POI_ICONS = {
  airports: Airplane, highways: Path, universities: GraduationCap, hospitals: FirstAid,
  tourism: Camera, commercial: Storefront, transport: Train,
};

// Read-only interactive nearby-POI list driven by a coordinate. Live OSM data
// (same source as the Place Profile). `onLocate(item)` focuses the parent map.
export default function NearbyPoi({ lat, lng, onLocate }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const hasCoords = !!lat && !!lng;
  const key = ["nearby-point", lat, lng];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/places/nearby-point", { params: { lat, lng } })).data,
    enabled: hasCoords,
    staleTime: 5 * 60 * 1000,
  });

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.get("/places/nearby-point", { params: { lat, lng, refresh: 1 } });
      qc.setQueryData(key, d);
    } catch (e) {
      toast.error(t("actionFailed", "Action failed"));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="nearby-poi">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl">{t("places.nearbyPoi", "Nearby points of interest")}</h3>
        {hasCoords && (
          <button type="button" onClick={refresh} data-testid="poi-refresh"
            className="text-xs text-slate-400 hover:text-[#0B2545] flex items-center gap-1 transition-colors">
            <ArrowClockwise size={13} className={refreshing ? "animate-spin" : ""} />{t("places.refreshNearby", "Refresh")}
          </button>
        )}
      </div>

      {!hasCoords ? (
        <p className="text-sm text-slate-400">{t("places.noNearby", "No nearby locations found")}</p>
      ) : isLoading ? (
        <p className="text-sm text-slate-400">{t("places.loadingNearby", "Loading nearby locations…")}</p>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-sm">
          {POI_FIELDS.map((k) => {
            const items = data?.categories?.[k] || [];
            const isOpen = expanded === k;
            const Icon = POI_ICONS[k];
            return (
              <div key={k}>
                <button type="button" data-testid={`poi-cat-${k}`}
                  onClick={() => setExpanded(isOpen ? null : k)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                  <span className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Icon size={18} className="text-[#C5A059]" weight="duotone" />
                    {t(`places.${k}`, k)}
                    <span className="text-xs text-slate-400">({items.length})</span>
                  </span>
                  <CaretDown size={16} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-3" data-testid={`poi-list-${k}`}>
                    {items.length === 0 ? (
                      <p className="text-sm text-slate-400 py-2">{t("places.noNearby", "No nearby locations found")}</p>
                    ) : (
                      <ul className="space-y-2">
                        {items.map((it, i) => (
                          <li key={i} data-testid={`poi-item-${k}-${i}`}
                            className="flex items-start justify-between gap-3 rounded-sm bg-slate-50/60 px-3 py-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{it.name}</div>
                              {it.address && <div className="text-xs text-slate-400 truncate">{it.address}</div>}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] uppercase tracking-wide text-slate-500 bg-white border border-slate-200 rounded-sm px-1.5 py-0.5">{it.distance_km} km</span>
                                {it.type && <span className="text-[10px] text-slate-400 capitalize">{it.type}</span>}
                              </div>
                            </div>
                            {onLocate && (
                              <button type="button" data-testid={`poi-locate-${k}-${i}`}
                                onClick={() => onLocate(it)}
                                className="shrink-0 text-xs text-[#0B2545] hover:text-[#C5A059] flex items-center gap-1 whitespace-nowrap transition-colors">
                                <MapPin size={13} />{t("places.locateOnMap", "View on map")}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
